/** Raw partnership-search usage as the seat-usage endpoint reports it. */
export interface PartnershipUsageSource {
  used: number;
  total: number;
}

/** The view a limit banner renders for the monthly partnership-search cycle. */
export interface LimitView {
  /** True when the plan grants an unbounded number of searches. */
  unlimited: boolean;
  used: number;
  total: number;
  /** Searches left this cycle; 0 when exhausted, 0 when unlimited (unused). */
  remaining: number;
  /** True once no searches remain for a bounded plan. */
  exhausted: boolean;
  /** Consumed share in [0, 1], or null when there is no ceiling. */
  ratio: number | null;
}

/**
 * Builds the monthly partnership-search limit view from reported usage.
 *
 * A non-positive total means the plan does not meter searches, which reads as
 * "unlimited" rather than "zero allowed" — the same convention the plan-tab
 * meters use. For a bounded plan the remainder is clamped at zero so an
 * over-count (addon drain, clock skew) never shows a negative figure, and the
 * exhausted flag drives the stronger upsell copy.
 *
 * @param usage - The reported used/total, or null when usage is unknown.
 * @returns The limit view, or null when usage could not be read.
 */
export function buildLimitView(usage: PartnershipUsageSource | null): LimitView | null {
  if (!usage) return null;
  const total = usage.total ?? 0;
  const used = Math.max(usage.used ?? 0, 0);
  if (total <= 0) {
    return { unlimited: true, used, total: 0, remaining: 0, exhausted: false, ratio: null };
  }
  const remaining = Math.max(total - used, 0);
  return {
    unlimited: false,
    used,
    total,
    remaining,
    exhausted: remaining <= 0,
    ratio: Math.min(used / total, 1),
  };
}
